import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { BOAPI } from '../service/UsersService';
import utils from '../utils/utils';

// אותה מוסכמת עמודות בדיוק כמו ImportBusinessesModal.jsx - ראו הערה שם
const COLUMNS = [
  { header: 'שם בית העסק *', field: 'name', required: true },
  { header: 'אחוז הנחה * (1-99)', field: 'discountPercent', required: true },
  { header: 'קטגוריה', field: 'category' },
  { header: 'פירוט ההטבה', field: 'benefitDescription' },
  { header: 'תיאור', field: 'description' },
  { header: 'עיר', field: 'city' },
  { header: 'כתובת', field: 'address' },
  { header: 'טלפון', field: 'phone' },
];

const MAX_ROWS = 500;

const EXAMPLE_ROW = {
  'שם בית העסק *': 'חנות לדוגמה',
  'אחוז הנחה * (1-99)': 10,
  'קטגוריה': 'ביגוד',
  'פירוט ההטבה': '10% הנחה לבני הישיבה',
  'תיאור': '',
  'עיר': 'בני ברק',
  'כתובת': 'רבי עקיבא 87',
  'טלפון': '03-1234567',
};

// ייבוא בתי עסק שכבר סוכם איתם בשטח, ישירות בסטטוס "ממתין לאישור בעלים" -
// בשונה מ-ImportBusinessesModal.jsx (שם כל שורה נכנסת לתור הצעות שדורש
// אישור admin נוסף), כאן כל שורה נכנסת ישירות ל-Businesses עם confirmToken
// מוכן, ואפשר להעתיק/לשלוח בוואטסאפ את הקישור לכל בית עסק מיד מתוך תוצאות
// הייבוא - ראו הערה מלאה ב-spBOImportPendingBusinesses/BO.sql
export default function ImportPendingBusinessesModal({ visible, onClose, onImported }) {
  const fileInputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [copiedRow, setCopiedRow] = useState(null);

  if (!visible) return null;

  const downloadSample = () => {
    const ws = XLSX.utils.json_to_sheet([EXAMPLE_ROW], { header: COLUMNS.map((c) => c.header) });
    ws['!cols'] = COLUMNS.map(() => ({ wch: 24 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'בתי עסק');
    XLSX.writeFile(wb, 'תבנית-ייבוא-בתי-עסק-ממתינים.xlsx');
  };

  const missingRequired = (row) => COLUMNS.filter((c) => c.required && !row[c.field]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setResults(null);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        const mapped = raw
          .map((r) => {
            const obj = {};
            COLUMNS.forEach((c) => { obj[c.field] = (r[c.header] ?? '').toString().trim(); });
            return obj;
          })
          .filter((r) => Object.values(r).some((v) => v !== ''));

        if (mapped.length === 0) {
          setError('לא נמצאו שורות בקובץ - ודאו שהכותרות תואמות לקובץ הדוגמה');
          setRows([]);
          return;
        }
        if (mapped.length > MAX_ROWS) {
          setError(`הקובץ מכיל ${mapped.length} שורות - ניתן לייבא עד ${MAX_ROWS} בכל פעם, פצלו לכמה קבצים`);
          setRows([]);
          return;
        }
        setRows(mapped);
      } catch (err) {
        console.error('Excel parse error:', err);
        setError('שגיאה בקריאת הקובץ - ודאו שזה קובץ Excel תקין (.xlsx)');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const submitImport = async () => {
    setImporting(true);
    setError('');
    try {
      const resp = await BOAPI.importPendingBusinesses(rows);
      if (resp?.[0]?.[0]?.code === 0) {
        setResults(resp[1] || []);
        onImported?.();
      } else {
        setError(resp?.[0]?.[0]?.msg || 'הייבוא נכשל');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setRows([]);
    setFileName('');
    setResults(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const close = () => {
    reset();
    onClose();
  };

  const linkFor = (row) => `${utils.BaseUrl}/confirm-business?token=${row.confirmToken}`;

  const copyLink = async (row) => {
    const link = linkFor(row);
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      window.prompt('העתיקו את הקישור:', link);
      return;
    }
    setCopiedRow(row.rowIndex);
    setTimeout(() => setCopiedRow((r) => (r === row.rowIndex ? null : r)), 2500);
  };

  const shareWhatsApp = (row) => {
    const text = `שלום, זה הקישור לאישור ההצטרפות של "${row.name}" למועדון איגוד: ${linkFor(row)}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  };

  const invalidCount = rows.filter((r) => missingRequired(r).length > 0).length;

  return (
    <div style={s.overlay} onClick={close}>
      <div style={s.card} onClick={(e) => e.stopPropagation()}>
        <div style={s.header}>
          <h3 style={s.title}>ייבוא בתי עסק ממתינים + קישור</h3>
          <button style={s.closeBtn} onClick={close}>✕</button>
        </div>

        <div style={s.body}>
          {!results ? (
            <>
              <div style={s.introBox}>
                <p style={s.introText}>
                  לבתי עסק שכבר סוכם איתם בשטח - כל שורה נכנסת מיד כבית עסק "ממתין לאישור בעלים",
                  ואחרי הייבוא תוכלו להעתיק/לשלוח לכל אחד את קישור האישור שלו. שם ואחוז הנחה הם
                  שדות חובה - שאר העמודות אופציונליות. מוגבל ל-{MAX_ROWS} בכל ייבוא.
                </p>
                <button style={s.sampleBtn} onClick={downloadSample}>⬇ הורדת קובץ לדוגמה</button>
              </div>

              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFile} style={s.fileInput} />

              {error && <div style={s.errorBanner}>❌ {error}</div>}

              {rows.length > 0 && (
                <>
                  <div style={s.summaryRow}>
                    <span>{fileName} - {rows.length} שורות</span>
                    {invalidCount > 0 && <span style={s.invalidBadge}>{invalidCount} שורות עם שדות חובה חסרים</span>}
                  </div>

                  <div style={s.previewWrapper}>
                    <table style={s.table}>
                      <thead>
                        <tr>
                          <th style={s.th}>#</th>
                          <th style={s.th}>שם</th>
                          <th style={s.th}>הנחה</th>
                          <th style={s.th}>סטטוס</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => {
                          const missing = missingRequired(r);
                          return (
                            <tr key={i}>
                              <td style={s.td}>{i + 1}</td>
                              <td style={s.td}>{r.name || '-'}</td>
                              <td style={s.td}>{r.discountPercent || '-'}</td>
                              <td style={s.td}>
                                {missing.length > 0 ? (
                                  <span style={s.rowError}>חסר: {missing.map((m) => m.header).join(', ')}</span>
                                ) : (
                                  <span style={s.rowOk}>תקין</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <button
                    style={{ ...s.submitBtn, ...((importing || invalidCount === rows.length) && s.submitBtnDisabled) }}
                    onClick={submitImport}
                    disabled={importing || rows.length === 0}
                  >
                    {importing ? 'מייבא...' : `ייבוא ${rows.length} בתי עסק`}
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              <div style={s.resultsSummary}>
                ✅ {results.filter((r) => r.success).length} נוספו כממתינים
                {results.some((r) => !r.success) && ` · ❌ ${results.filter((r) => !r.success).length} נכשלו`}
              </div>
              <div style={s.previewWrapper}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>#</th>
                      <th style={s.th}>שם</th>
                      <th style={s.th}>תוצאה</th>
                      <th style={s.th}>קישור אישור</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r) => (
                      <tr key={r.rowIndex}>
                        <td style={s.td}>{r.rowIndex}</td>
                        <td style={s.td}>{r.name || '-'}</td>
                        <td style={s.td}>
                          {r.success ? (
                            <span style={s.rowOk}>{r.warning ? `⚠️ ${r.warning}` : 'נוסף בהצלחה'}</span>
                          ) : (
                            <span style={s.rowError}>{r.errorMsg}</span>
                          )}
                        </td>
                        <td style={s.td}>
                          {r.success ? (
                            <div style={s.linkActions}>
                              <button style={s.linkBtn} onClick={() => copyLink(r)}>
                                {copiedRow === r.rowIndex ? '✓ הועתק' : '🔗 העתקה'}
                              </button>
                              <button style={s.linkBtn} onClick={() => shareWhatsApp(r)}>📲 וואטסאפ</button>
                            </div>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button style={s.submitBtn} onClick={reset}>ייבוא קובץ נוסף</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 100, padding: 16, direction: 'rtl',
  },
  card: {
    width: '100%', maxWidth: 700, maxHeight: '88vh', display: 'flex', flexDirection: 'column',
    background: '#fff', borderRadius: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px', borderBottom: '1px solid #e3e8ee', flexShrink: 0,
  },
  title: { margin: 0, fontSize: 16, fontWeight: 700, color: '#1a1f36' },
  closeBtn: { background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', color: '#697386' },
  body: { padding: 20, overflowY: 'auto', flex: 1 },

  introBox: {
    background: '#fffaf0', borderRadius: 14, padding: 16, marginBottom: 16,
    display: 'flex', flexDirection: 'column', gap: 12, border: '1px solid #f0e0b8',
  },
  introText: { margin: 0, fontSize: 13, color: '#1a1f36', lineHeight: 1.6 },
  sampleBtn: {
    alignSelf: 'flex-start', padding: '9px 16px', background: '#fff', color: '#8a6d1a',
    border: '1.5px solid #e0cd97', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700,
  },

  fileInput: { width: '100%', marginBottom: 14, fontSize: 13 },

  errorBanner: { background: '#ffebee', color: '#c62828', padding: 12, borderRadius: 8, marginBottom: 14, fontSize: 13 },

  summaryRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8,
    fontSize: 13, fontWeight: 700, color: '#1a1f36', marginBottom: 10,
  },
  invalidBadge: { background: '#fff3e0', color: '#e65100', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700 },

  previewWrapper: { maxHeight: 300, overflowY: 'auto', overflowX: 'auto', borderRadius: 10, border: '1px solid #e3e8ee', marginBottom: 16 },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' },
  th: { padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#4f566b', fontSize: 12, borderBottom: '2px solid #e3e8ee', background: '#f8fafc', position: 'sticky', top: 0 },
  td: { padding: '9px 12px', color: '#4f566b', fontSize: 12.5, textAlign: 'right', borderBottom: '1px solid #f0f2f5' },
  rowOk: { color: '#2e7d32', fontWeight: 700 },
  rowError: { color: '#c62828', fontWeight: 700 },

  linkActions: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  linkBtn: { padding: '5px 10px', background: '#fff', color: '#4f566b', border: '1.5px solid #e3e8ee', borderRadius: 8, cursor: 'pointer', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' },

  resultsSummary: { fontSize: 14, fontWeight: 700, color: '#1a1f36', marginBottom: 14 },

  submitBtn: { width: '100%', padding: '13px 16px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer', fontSize: 14, fontWeight: 700 },
  submitBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
};
